import React, { useState } from 'react';
import {
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Search,
  ArrowLeft,
  Landmark,
  User,
  ShieldCheck,
  Sparkles
} from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
  tip?: string;
}

interface FAQCategory {
  title: string;
  icon: React.ReactNode;
  items: FAQItem[];
}

interface FAQViewProps {
  onBack: () => void;
}

export const FAQView: React.FC<FAQViewProps> = ({ onBack }) => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');

  const toggleItem = (categoryIndex: number, itemIndex: number) => {
    const key = `${categoryIndex}-${itemIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const categories: FAQCategory[] = [
    {
      title: "The NatureNani Difference",
      icon: <Landmark size={20} className="text-emerald-700" />,
      items: [
        {
          question: "What makes NatureNani different from other wellness tools?",
          answer: <span>
            Most wellness apps give you generic tips or track your symptoms. NatureNani teaches you why your body is responding the way it is.
            <br /><br />
            We draw from hundreds of ancient healing texts — Ayurveda, Naturopathy, Homeopathy, and Acupressure — that look at patterns Western medicine often overlooks, like how your digestion affects your energy, why you crave certain foods, or what your skin is telling you about your internal health.
            <br /><br />
            Instead of just saying "eat healthy," we help you understand your unique body signals and what they mean. You'll learn to recognize early warning signs, understand root causes (not just symptoms), and make informed choices — whether you use natural remedies alone or alongside conventional medicine.
            <br /><br />
            Think of it as health education that empowers you to be an active participant in your healing, not just a passive patient.
          </span>
        },
        {
          question: "What is Body Wisdom, and why does it matter?",
          answer: <span>
            Your body is constantly communicating with you. When you eat something that doesn't agree with you, it sends signals. When you're stressed, it shows up physically. When you're low on sleep, it affects your digestion, mood, and immunity.
            <br /><br />
            Most of us have lost touch with this inner intelligence. We ignore the signals, push through fatigue, or take something to make symptoms disappear — without ever asking why they appeared in the first place.
            <br /><br />
            Developing your Body Wisdom means learning to:
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>Recognize early warning signs before they become bigger problems</li>
              <li>Understand what your cravings, energy dips, and aches are really telling you</li>
              <li>Know which foods, habits, and practices support your unique body</li>
              <li>Trust your body's signals instead of fearing them</li>
              <li>Make informed choices about your health with confidence</li>
            </ul>
            <br />
            NatureNani helps you build this intelligence by teaching you to decode your body's language — so you become the expert on your own health.
          </span>
        },
        {
          question: "Why focus on the \"root cause\" instead of just treating symptoms?",
          answer: <span>
            Think of symptoms as your body's alarm system. You can silence the alarm — with a painkiller, for example — but if you don't find out why it's ringing, the problem keeps growing.
            <br /><br />
            In Ayurveda and Naturopathy, symptoms aren't the problem. They're messengers telling you something is out of balance. That headache might be signaling poor digestion. That skin rash could mean your body is overloaded with toxins. That persistent fatigue might stem from the way you're eating or sleeping.
            <br /><br />
            When you only treat the symptom, you silence the messenger — but the imbalance remains. Over time, a small imbalance can grow into a chronic condition.
            <br /><br />
            NatureNani helps you ask "Why?" — why is this happening, what's out of balance, and what does my body actually need? By identifying the root cause, whether it's digestive weakness, environmental factors, stress, or lifestyle habits, you can restore genuine balance and prevent small issues from becoming lifelong challenges.
            <br /><br />
            Real healing happens when you address the source, not just the signal.
          </span>
        },
        {
          question: "How long does it take to see results from Ayurvedic or Naturopathic remedies?",
          answer: "Natural healing is a journey of rebalancing the body, not just masking symptoms. While you may notice subtle shifts in energy, digestion, or sleep within the first 3-5 days, deeper structural balancing usually takes 3 to 4 weeks. Consistency is key!"
        }
      ]
    },
    {
      title: "Your Personalized Journey",
      icon: <User size={20} className="text-amber-600" />,
      items: [
        {
          question: "Why does NatureNani need my personal profile and physical constitution?",
          answer: "Every body is a unique ecosystem. In traditions like Ayurveda, your age, environment, and natural constitution (known as Prakriti) determine how you process the world around you. We also collect your dietary preferences and allergens to ensure every food or herbal suggestion is safe and tailored specifically to you."
        },
        {
          question: "What do I get with a Premium membership?",
          answer: "Premium is designed for those ready to go deeper into their health journey. It unlocks the full Nani Circle, which includes unlimited consultations for deep-dive healing, detailed natural protocols with precise herbal guidance from Botanical Rx, and full learning modules to build your Body Wisdom every day."
        },
        {
          question: "How do I track my progress?",
          answer: "As you engage with our learning modules — Consultation, Nutri Heal, and Yoga Aid — you'll begin noticing patterns in how your body responds to natural changes. Over time, these habits strengthen your Body Wisdom, which is the truest measure of your long-term health and resilience."
        }
      ]
    },
    {
      title: "Safety, Trust & Privacy",
      icon: <ShieldCheck size={20} className="text-blue-600" />,
      items: [
        {
          question: "Is NatureNani a replacement for my doctor?",
          answer: "No. We believe in a \"Both/And\" approach to health. NatureNani is an educational and wellness companion designed to supplement — not replace — your medical care. Our goal is to deepen your understanding of your body so you can ask better questions and have more informed conversations with your healthcare providers."
        },
        {
          question: "Can I use NatureNani if I have food allergies?",
          answer: "Yes. Your safety is built into our intelligence engine. Once you update your profile with your allergens, NatureNani automatically filters all dietary and botanical suggestions to ensure they are safe for you to explore."
        },
        {
          question: "Is my health data private?",
          answer: "Completely. We use industry-standard encryption and secure authentication to protect your information. Your data is used solely to generate your personalized protocols — we do not sell your health information or personal data to third-party advertisers.",
          tip: "Nani's Tip: If you're unsure where to start, try telling me about your current energy levels in your next consultation. Sometimes the smallest signals lead to the greatest wisdom!"
        }
      ]
    }
  ];

  // Search Logic
  const filteredCategories = categories.map((cat, cIdx) => {
    // Check if category title matches
    const titleMatch = cat.title.toLowerCase().includes(searchQuery.toLowerCase());

    // Check items
    const matchingItems = cat.items.filter(item =>
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (typeof item.answer === 'string' && item.answer.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // If title matches, show all items (or maybe just matching ones? Usually better to show relevant ones)
    // Let's show all items if title matches, otherwise just matching items.
    // Actually, simple filtering logic:
    // If search is empty, return everything.
    // If not, return items that match, BUT we also want to keep the Category structure.

    if (!searchQuery) return { ...cat, originalIndex: cIdx };

    // If we have matching items, return them.
    if (matchingItems.length > 0) {
      return { ...cat, items: matchingItems, originalIndex: cIdx };
    }

    // If category title matches, return all items
    if (titleMatch) {
      return { ...cat, items: cat.items, originalIndex: cIdx };
    }

    // If category title matches but no items match specifically, maybe show all items?
    // Or just show the category? Let's assume we search within content mostly.
    return null;
  }).filter(Boolean) as (FAQCategory & { originalIndex: number })[];


  return (
    <div className="h-full bg-sage-50 overflow-y-auto pb-20 scrollbar-hide">
      <div className="max-w-2xl mx-auto p-6 md:p-8 space-y-8">

        {/* Intro */}
        <div className="text-center space-y-3 py-4">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-sage-900 leading-tight">
            How can Nani help?
          </h2>
          <p className="text-sage-600 font-medium">
            Search our ancient wisdom archives or explore topics below.
          </p>
        </div>

        {/* Search Bar */}
        <div className="relative group">
          <input
            type="text"
            placeholder="Search for answers (e.g., 'privacy', 'dosha')..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-4 rounded-2xl border border-sage-200 bg-white shadow-sm group-hover:shadow-md focus:ring-2 focus:ring-sage-400/50 focus:border-sage-400 outline-none transition-all text-sage-900 placeholder:text-sage-400"
          />
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400 group-focus-within:text-sage-600 transition-colors" size={20} />
        </div>

        {/* Categories */}
        <div className="space-y-8">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="text-sage-400" size={24} />
              </div>
              <p className="text-sage-600 font-medium">No answers found for "{searchQuery}"</p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-2 text-emerald-600 font-bold hover:underline text-sm"
              >
                Clear Search
              </button>
            </div>
          ) : (
            filteredCategories.map((category) => (
              <div key={category.title} className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center gap-3 px-2 border-b border-sage-200/50 pb-2">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    {category.icon}
                  </div>
                  <h3 className="font-serif text-lg font-bold text-sage-900">
                    {category.title}
                  </h3>
                </div>

                <div className="space-y-3">
                  {category.items.map((item, index) => {
                    const uniqueIndex = `${category.originalIndex}-${index}`; // This logic might fail if items are filtered.
                    // Better to use item title or question as part of key if unique.
                    // For state tracking, let's just use the question string as key to be safe across searches.
                    const isOpen = openItems[item.question];

                    return (
                      <div
                        key={item.question}
                        className="bg-white rounded-2xl border border-sage-100 overflow-hidden shadow-sm hover:shadow-md hover:border-sage-200 transition-all duration-300"
                      >
                        <button
                          onClick={() => setOpenItems(prev => ({ ...prev, [item.question]: !prev[item.question] }))}
                          className="w-full flex items-start justify-between p-5 text-left gap-4"
                        >
                          <span className={`font-semibold transition-colors ${isOpen ? 'text-emerald-800' : 'text-sage-800'}`}>
                            {item.question}
                          </span>
                          <div className={`p-1 rounded-full bg-sage-50 transition-transform duration-300 ${isOpen ? 'rotate-180 bg-emerald-50 text-emerald-600' : 'text-sage-400'}`}>
                            <ChevronDown size={20} />
                          </div>
                        </button>

                        {isOpen && (
                          <div className="px-5 pb-6 animate-in slide-in-from-top-2 duration-300">
                            <div className="text-sage-600 leading-relaxed">
                              {item.answer}
                            </div>

                            {item.tip && (
                              <div className="mt-4 bg-amber-50/50 rounded-xl p-4 border border-amber-100 flex gap-4">
                                <div className="p-2 bg-white rounded-full h-fit shadow-sm">
                                  <Sparkles size={16} className="text-amber-500 fill-amber-500" />
                                </div>
                                <div>
                                  <span className="font-bold text-xs uppercase tracking-wider text-amber-800 mb-1 block">
                                    Nani's Tip
                                  </span>
                                  <p className="text-sm text-amber-900/80 font-medium italic">
                                    "{item.tip.replace("Nani's Tip: ", "")}"
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Support Section */}
        <div className="bg-gradient-to-br from-emerald-800 to-sage-800 rounded-3xl p-8 text-white text-center shadow-xl relative overflow-hidden mt-8">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-x-16 -translate-y-16"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-400/10 rounded-full blur-2xl -translate-x-12 translate-y-12"></div>

          <div className="relative z-10">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
              <HelpCircle className="text-white" size={24} />
            </div>
            <h3 className="text-xl font-serif font-bold mb-2">Still have questions?</h3>
            <p className="text-sage-100 text-sm mb-6 max-w-xs mx-auto opacity-90">
              Our wise archives are always expanding. Feel free to ask Nani anything during your next consultation.
            </p>
            <button
              onClick={onBack}
              className="bg-white text-emerald-900 px-6 py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-emerald-50 transition-all shadow-lg active:scale-95"
            >
              Start a Consultation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
